import { Dropdown } from 'antd';
import React, { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Avatar, AvatarGroup } from 'components/atoms/avatar';
import { Menu } from 'components/atoms/dropdown';
import { ProjectPlanFilterOption } from 'data/projects';
import { ResponsibleFilterIcon, FilterTag } from './filters.style';

const RemainingOptionsFilterTag = styled(FilterTag)`
  cursor: pointer;
  .ant-avatar-icon {
    width: unset;
    border-radius: 0;
    background-color: transparent !important;
    margin-right: var(--spacing-03);
  }
  ${AvatarGroup} {
    .ant-avatar {
      box-shadow: none;
    }
  }
`;
const MenuItem = styled(Menu.Item)`
  padding: var(--spacing-03) var(--spacing-05) !important;
`;

interface Props {
  filterOptions: ProjectPlanFilterOption[];
  selected: ProjectPlanFilterOption;
  onChange: (next: ProjectPlanFilterOption) => void;
}

export const ResponsibleFilter: FC<Props> = ({
  filterOptions,
  selected,
  onChange,
}) => {
  const reorderedFilterOptions = useMemo(() => {
    const selectedOptionIndex = filterOptions.findIndex(
      ({ value }) => value === selected.value,
    );

    if (selectedOptionIndex === -1 || selectedOptionIndex < 3) {
      return [...filterOptions];
    }

    return filterOptions.reduce((result, filter, currentIndex) => {
      if (currentIndex === selectedOptionIndex) {
        return result;
      }
      if (currentIndex === 1) {
        return result.concat([filter, filterOptions[selectedOptionIndex]]);
      }

      return result.concat([filter]);
    }, [] as ProjectPlanFilterOption[]);
  }, [filterOptions, selected.value]);

  const firstThreeSecondaryFilters = useMemo(
    () => reorderedFilterOptions.slice(0, 3),
    [reorderedFilterOptions],
  );
  const remainingSecondaryFilters = useMemo(
    () => reorderedFilterOptions.slice(3),
    [reorderedFilterOptions],
  );

  return (
    <>
      <FormattedMessage id="responsible" />
      <ResponsibleFilterIcon />
      {firstThreeSecondaryFilters.map((option) => (
        <FilterTag
          key={option.value}
          color={option.value === selected.value ? 'blue' : 'cool-gray'}
          label={option.label}
          imageUrl={option.companyLogoUrl}
          onClick={(e) => {
            e.stopPropagation();
            onChange(option);
          }}
          hoverColor="blue"
        />
      ))}
      {remainingSecondaryFilters.length > 0 ? (
        <Dropdown
          trigger={['hover']}
          className="more-responsible-filters"
          overlay={
            <Menu selectedKeys={[selected.value.toString()]}>
              {remainingSecondaryFilters.map((option) => (
                <MenuItem
                  key={option.value.toString()}
                  icon={
                    <Avatar name={option.label} src={option.companyLogoUrl} />
                  }
                  onClick={() => onChange(option)}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Menu>
          }
        >
          <RemainingOptionsFilterTag
            icon={
              <AvatarGroup maxCount={2}>
                {remainingSecondaryFilters.slice(0, 2).map((option) => (
                  <Avatar
                    key={option.value}
                    name={option.label}
                    src={option.companyLogoUrl}
                    size="small"
                  />
                ))}
              </AvatarGroup>
            }
            label={`+${remainingSecondaryFilters.length}`}
            hoverColor="blue"
          />
        </Dropdown>
      ) : null}
    </>
  );
};
